import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa";
import { FaChevronDown, FaChevronUp, FaDownload } from "react-icons/fa"; // Added FaDownload
import { UserContext } from "../context/UserContext";

const FundingReport = ({ onClose }) => {
  const [reports, setReports] = useState([]);
  const { emailId, isLoggedIn } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [activeDownloadMonth, setActiveDownloadMonth] = useState(null); // New state for tracking active dropdown

  useEffect(() => {
    if (!isLoggedIn || emailId !== "info@urcommunity.com.au") return;

    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const response = await fetch("/api/funding-reports");
        if (!response.ok) throw new Error("Failed to fetch funding reports");
        const data = await response.json();
        setReports(data);
        
        // Set current month as expanded by default
        const currentDate = new Date();
        const currentMonthYear = `${currentDate.toLocaleString('default', { month: 'short' })}-${currentDate.getFullYear()}`;
        setExpandedMonths(new Set([currentMonthYear]));
      } catch (error) {
        console.error("Error fetching funding reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [emailId, isLoggedIn]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const toggleMonth = (monthYear) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthYear)) {
        newSet.delete(monthYear);
      } else {
        newSet.add(monthYear);
      }
      return newSet;
    });
  };

  const groupDonationsByMonthAndProject = (donations) => {
    return donations.reduce((acc, donation) => {
      const date = new Date(donation.timestamp);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })}-${date.getFullYear()}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = {};
      }
      
      if (!acc[monthYear][donation.project_name]) {
        acc[monthYear][donation.project_name] = {
          totalAmount: 0,
          projectCreator: donation.funder,
          donationCount: 0
        };
      }
      
      acc[monthYear][donation.project_name].totalAmount += parseFloat(donation.amount);
      acc[monthYear][donation.project_name].donationCount += 1;
      
      return acc;
    }, {});
  };

  const handleDownload = async (monthYear, format) => {
    try {
      const response = await fetch(
        `/api/download-funding-report?monthYear=${monthYear}&format=${format}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const fileExtension = format.toLowerCase() === 'excel' ? 'xlsx' : format.toLowerCase();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `funding_report_${monthYear}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setActiveDownloadMonth(null); // Close dropdown after successful download
    } catch (error) {
      console.error('Error downloading report:', error);
      // Optionally add error handling UI feedback here
    }
  };

  // Add function to calculate totals
  const calculateTotals = (projects) => {
    return Object.values(projects).reduce((acc, data) => {
      return {
        totalAmount: acc.totalAmount + data.totalAmount,
        totalDonations: acc.totalDonations + data.donationCount
      };
    }, { totalAmount: 0, totalDonations: 0 });
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h3 style={{ margin: 0 }}>Project Funding Report</h3>
          <FaTimes onClick={onClose} style={{ cursor: "pointer", fontSize: "1.5rem" }} />
        </div>

        {isLoading ? (
          <p style={{ textAlign: "center" }}>Loading funding reports...</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {Object.entries(groupDonationsByMonthAndProject(reports))
              .sort((a, b) => new Date(b[0]) - new Date(a[0]))
              .map(([monthYear, projects]) => (
                <div key={monthYear} style={{ marginBottom: "15px" }}>
                  <div 
                    onClick={() => toggleMonth(monthYear)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    <div 
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <h4 
                        style={{ 
                          margin: 0,
                          display: "flex",
                          alignItems: "center",
                          position: "relative" // Added for dropdown positioning
                        }}
                      >
                        {monthYear}
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <FaDownload 
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDownloadMonth(activeDownloadMonth === monthYear ? null : monthYear);
                            }}
                            style={{ 
                              cursor: "pointer", 
                              marginLeft: "10px",
                              color: "#007bff",
                              fontSize: "1.2rem"
                            }} 
                          />
                          {activeDownloadMonth === monthYear && (
                            <div 
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "0",
                                backgroundColor: "white",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                zIndex: 1000,
                                minWidth: "100px",
                                fontSize: "13px"
                              }}
                            >
                              {[
                                { label: "PDF Format", value: "PDF" },
                                { label: "CSV", value: "CSV" },
                                { label: "Excel File", value: "Excel" }
                              ].map((format) => (
                                <div
                                  key={format.value}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(monthYear, format.value);
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #dee2e6",
                                    fontSize: "13px",
                                    color: "#333",
                                    "&:last-child": {
                                      borderBottom: "none"
                                    },
                                    "&:hover": {
                                      backgroundColor: "#f8f9fa"
                                    }
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  {format.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </h4>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                      {expandedMonths.has(monthYear) ? 
                        <FaChevronUp color="red" /> : 
                        <FaChevronDown color="red" />
                      }
                    </div>
                  </div>
                  
                  {expandedMonths.has(monthYear) && (
                    <div style={{ marginTop: "10px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                            <th style={{ padding: "10px", textAlign: "left" }}>Project Name</th>
                            <th style={{ padding: "10px", textAlign: "left" }}>Project Creator</th>
                            <th style={{ padding: "10px", textAlign: "right" }}>Total Amount by month</th>
                            <th style={{ padding: "10px", textAlign: "center" }}>Number of Donations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(projects).map(([projectName, data]) => (
                            <tr key={projectName} style={{ borderBottom: "1px solid #dee2e6" }}>
                              <td style={{ padding: "10px" }}>{projectName}</td>
                              <td style={{ padding: "10px" }}>{data.projectCreator}</td>
                              <td style={{ padding: "10px", textAlign: "right" }}>
                                {formatCurrency(data.totalAmount)}
                              </td>
                              <td style={{ padding: "10px", textAlign: "center" }}>
                                {data.donationCount}
                              </td>
                            </tr>
                          ))}
                          {/* Add total row */}
                          {Object.keys(projects).length > 0 && (
                            <tr style={{ 
                              borderTop: "2px solid #dee2e6",
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold"
                            }}>
                              <td colSpan="2" style={{ padding: "10px", textAlign: "right" }}>Total:</td>
                              <td style={{ padding: "10px", textAlign: "right" }}>
                                {formatCurrency(calculateTotals(projects).totalAmount)}
                              </td>
                              <td style={{ padding: "10px", textAlign: "center" }}>
                                {calculateTotals(projects).totalDonations}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FundingReport;
